"use client";

import { useState, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { StarRating } from "@/components/product/star-rating";
import { ReviewList } from "@/components/product/review-list";
import { ReviewForm } from "@/components/product/review-form";
import { MessageSquare } from "lucide-react";
import type { ProductReviewsData } from "@/types";

interface ReviewsSectionProps {
  slug: string;
  onCountChange?: (count: number) => void;
}

export function ReviewsSection({ slug, onCountChange }: ReviewsSectionProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { data, isLoading } = useQuery<ProductReviewsData>({
    queryKey: ["reviews", slug],
    queryFn: async () => {
      const res = await api.get(`/api/catalog/products/${slug}/reviews`);
      return res.data;
    },
  });

  useEffect(() => {
    if (data && onCountChange) onCountChange(data.summary.totalReviews);
  }, [data, onCountChange]);

  const handleSuccess = useCallback(() => {
    setShowForm(false);
    setSubmitted(true);
    queryClient.invalidateQueries({ queryKey: ["reviews", slug] });
  }, [queryClient, slug]);

  return (
    <div>
      {/* Özet + aksiyon */}
      <div className="mb-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between md:mb-8">
        <div className="flex min-w-0 flex-wrap items-center gap-2 md:gap-3">
          {data && data.summary.totalReviews > 0 ? (
            <div className="flex items-center gap-1.5">
              <StarRating rating={data.summary.averageRating} size={14} />
              <span className="text-sm text-gray-500">
                {data.summary.averageRating.toFixed(1)} ({data.summary.totalReviews})
              </span>
            </div>
          ) : (
            <span className="text-sm text-gray-500">İlk değerlendirmeyi siz yapın.</span>
          )}
        </div>
        {!showForm && !submitted && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-[var(--sf-radius)] border border-gray-300 px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:border-gray-900 sm:w-auto sm:py-2"
          >
            <MessageSquare className="h-4 w-4" />
            Değerlendirme Yaz
          </button>
        )}
      </div>

      {showForm && (
        <div className="mb-8">
          <ReviewForm slug={slug} onSuccess={handleSuccess} />
        </div>
      )}

      {submitted && (
        <div className="mb-6 p-4 bg-green-50 text-green-700 text-sm rounded-[var(--sf-radius)] border border-green-100">
          Değerlendirmeniz için teşekkürler! Yayınlandı.
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse space-y-2">
              <div className="h-4 bg-gray-100 rounded w-1/4" />
              <div className="h-3 bg-gray-100 rounded w-full" />
            </div>
          ))}
        </div>
      ) : data && data.summary.totalReviews > 0 ? (
        <ReviewList data={data} />
      ) : (
        <div className="rounded-[var(--sf-radius)] border border-gray-100 bg-gray-50 px-4 py-3">
          <p className="text-sm text-gray-500">Henüz yorum yok.</p>
        </div>
      )}
    </div>
  );
}
